import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "@/components/base/Modal";
import { Button } from "@/components/base/Button";
import { Badge } from "@/components/base/Badge";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import {
  getSettleDuesPreview,
  autoDeductCycleAdvance,
  type SettleDuesPreviewData,
} from "@/services/collections";
import { useToast } from "@/context/ToastContext";

interface CollectionCompleteModalProps {
  open: boolean;
  onClose: () => void;
  cycleId: string;
  onSuccess?: () => void;
}

export function CollectionCompleteModal({
  open,
  onClose,
  cycleId,
  onSuccess,
}: CollectionCompleteModalProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState<SettleDuesPreviewData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [completedResult, setCompletedResult] = useState<{
    count: number;
    totalDeducted: number;
  } | null>(null);
  const [countdown, setCountdown] = useState(4);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!open || !cycleId) {
      setCompletedResult(null);
      setCountdown(4);
      return;
    }
    setCompletedResult(null);
    setCountdown(4);
    setLoading(true);
    setError(null);
    getSettleDuesPreview(cycleId)
      .then((res) => setData(res))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load dues preview"))
      .finally(() => setLoading(false));
  }, [open, cycleId]);

  const handleFinish = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    onSuccess?.();
    onClose();
    navigate("/collection");
  };

  // Auto-redirect countdown when cycle is completed
  useEffect(() => {
    if (!completedResult) return;
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          handleFinish();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [completedResult]);

  const handleConfirm = async () => {
    if (!cycleId) return;
    setSubmitting(true);
    try {
      const res = await autoDeductCycleAdvance(cycleId);
      toast.success(
        "Cycle Collection Settled",
        `Successfully settled ${res.count} member installments totaling ₹${res.totalDeducted.toLocaleString("en-IN")}.`
      );
      setCompletedResult({
        count: res.count,
        totalDeducted: res.totalDeducted,
      });
    } catch (err) {
      toast.error(
        "Settlement Failed",
        err instanceof Error ? err.message : "Could not complete cycle settlement."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={completedResult ? handleFinish : onClose}
      title={completedResult ? "Advance Deductions Processed" : "Settle Dues via Available Advance"}
      description={
        completedResult
          ? `${data?.groupName ?? ""} (${data?.groupCode ?? ""}) · Cycle ${data?.cycleNo ?? ""} advance deductions completed`
          : data
          ? `${data.groupName} (${data.groupCode}) · Cycle ${data.cycleNo} (${data.periodLabel})`
          : "Settle pending installments using available advance wallet funds"
      }
      size="xl"
      footer={
        completedResult ? (
          <div className="flex w-full items-center justify-between">
            <span className="text-xs text-foreground-500">
              Redirecting to collection page in <strong className="text-primary-600 font-semibold">{countdown}s</strong>...
            </span>
            <Button
              variant="primary"
              icon={<i className="ri-arrow-right-line" />}
              onClick={handleFinish}
            >
              Go to Collections Now
            </Button>
          </div>
        ) : (
          <div className="flex w-full items-center justify-between">
            <div className="text-xs text-foreground-500">
              {data ? `${data.sufficientCount + (data.partialCount ?? 0)} member(s) can be settled from available advance.` : ""}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirm}
                loading={submitting}
                disabled={loading || !data || data.totalPendingMembers === 0 || (data.sufficientCount === 0 && (data.partialCount ?? 0) === 0)}
              >
                Confirm & Auto-Deduct Available
              </Button>
            </div>
          </div>
        )
      }
    >
      {completedResult ? (
        <div className="space-y-5 py-2">
          <div className="flex flex-col items-center text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success-100 text-success-600 mb-3 shadow-inner">
              <i className="ri-checkbox-circle-fill text-3xl" />
            </div>
            <h3 className="text-lg font-bold text-foreground-900">
              Advance Deductions Completed!
            </h3>
            <p className="mt-1 text-sm text-foreground-600 max-w-md">
              Available advance balances were successfully deducted. The cycle collection rate has updated to reflect actual collected funds.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-success-200 bg-success-50/50 p-4 text-center">
              <span className="text-xs font-semibold uppercase tracking-wider text-success-800">
                Total Deducted
              </span>
              <div className="mt-1 text-xl font-bold text-success-700">
                <CurrencyDisplay amount={completedResult.totalDeducted} />
              </div>
              <span className="text-xs text-success-600">From available member wallets</span>
            </div>

            <div className="rounded-xl border border-primary-200 bg-primary-50/50 p-4 text-center">
              <span className="text-xs font-semibold uppercase tracking-wider text-primary-800">
                Members Deducted
              </span>
              <div className="mt-1 text-xl font-bold text-primary-700">
                {completedResult.count} Installment(s)
              </div>
              <span className="text-xs text-primary-600">Updated with real funds</span>
            </div>
          </div>
        </div>
      ) : loading ? (
        <div className="flex flex-col items-center justify-center py-12 space-y-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
          <p className="text-sm text-foreground-500">Calculating dues and available advance balances…</p>
        </div>
      ) : error ? (
        <div className="rounded-lg border border-danger-200 bg-danger-50 p-4 text-sm text-danger-700">
          <p className="font-semibold">Error loading preview</p>
          <p>{error}</p>
        </div>
      ) : !data || data.totalPendingMembers === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <div className="h-12 w-12 rounded-full bg-success-50 text-success-600 flex items-center justify-center mb-3">
            <i className="ri-checkbox-circle-line text-2xl" />
          </div>
          <h3 className="font-semibold text-foreground-900">All Installments Already Paid</h3>
          <p className="mt-1 text-sm text-foreground-500 max-w-sm">
            There are no outstanding installments for this cycle. The collection rate is 100%.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-background-200 bg-background-50 p-3.5">
              <span className="text-xs font-medium text-foreground-500">Total Pending Dues</span>
              <div className="mt-1 text-lg font-bold text-foreground-900">
                <CurrencyDisplay amount={Number(data.totalAmountDue)} />
              </div>
              <span className="text-xs text-foreground-500">{data.totalPendingMembers} member(s)</span>
            </div>

            <div className="rounded-lg border border-success-200 bg-success-50/50 p-3.5">
              <span className="text-xs font-medium text-success-800">Available from Advance</span>
              <div className="mt-1 text-lg font-bold text-success-700">
                <CurrencyDisplay amount={Number(data.totalCanDeduct ?? 0)} />
              </div>
              <span className="text-xs text-success-600">{data.sufficientCount + (data.partialCount ?? 0)} member(s) covered</span>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3.5">
              <span className="text-xs font-medium text-amber-800">Requires Actual Money</span>
              <div className="mt-1 text-lg font-bold text-amber-700">
                <CurrencyDisplay amount={Math.max(0, Number(data.totalAmountDue) - Number(data.totalCanDeduct ?? 0))} />
              </div>
              <span className="text-xs text-amber-600">{data.noBalanceCount ?? (data.totalPendingMembers - data.sufficientCount)} member(s) without funds</span>
            </div>
          </div>

          {/* Explanation Alert */}
          <div className="rounded-lg border border-primary-200 bg-primary-50/60 p-3 text-xs text-primary-900 flex items-start gap-2.5">
            <i className="ri-information-line text-base text-primary-600 flex-shrink-0 mt-0.5" />
            <div>
              <span className="font-semibold">Actual Collection Accuracy:</span> Only members with genuine available advance wallet funds will be deducted. Installments for members without sufficient funds will <strong className="font-semibold">remain pending/overdue</strong> until actual payment is received (via Cash, UPI, Bank Transfer). Wallet overdrafts are prevented to keep the collection completion rate 100% accurate.
            </div>
          </div>

          {/* Members Table */}
          <div className="overflow-hidden rounded-lg border border-background-200">
            <div className="max-h-60 overflow-y-auto">
              <table className="w-full text-left text-xs">
                <thead className="sticky top-0 bg-background-100 text-foreground-600 font-semibold border-b border-background-200">
                  <tr>
                    <th className="py-2.5 px-3">Seat</th>
                    <th className="py-2.5 px-3">Member</th>
                    <th className="py-2.5 px-3 text-right">Installment Due</th>
                    <th className="py-2.5 px-3 text-right">Available Advance</th>
                    <th className="py-2.5 px-3 text-right">Deductible</th>
                    <th className="py-2.5 px-3 text-right">Remaining Due</th>
                    <th className="py-2.5 px-3 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-100">
                  {data.members.map((m) => {
                    const due = Number(m.dueAmount);
                    const curr = Number(m.currentBalance);
                    const canDeduct = Number(m.deductibleAmount ?? Math.min(due, Math.max(0, curr)));
                    const remDue = Number(m.remainingDue ?? Math.max(0, due - canDeduct));
                    const isCovered = curr >= due;
                    const isPartial = curr > 0 && curr < due;

                    return (
                      <tr key={m.installmentId} className="hover:bg-background-50">
                        <td className="py-2 px-3 font-medium text-foreground-700">T-{m.ticketNo}</td>
                        <td className="py-2 px-3">
                          <div className="font-medium text-foreground-900">{m.memberName}</div>
                          <div className="text-[11px] text-foreground-400">{m.memberCode}</div>
                        </td>
                        <td className="py-2 px-3 text-right font-medium text-foreground-900">
                          <CurrencyDisplay amount={due} hideSymbol />
                        </td>
                        <td className="py-2 px-3 text-right text-foreground-700">
                          <CurrencyDisplay amount={curr} hideSymbol className={curr <= 0 ? "text-foreground-400" : "text-success-700 font-medium"} />
                        </td>
                        <td className="py-2 px-3 text-right font-semibold text-primary-700">
                          <CurrencyDisplay amount={canDeduct} hideSymbol />
                        </td>
                        <td className="py-2 px-3 text-right font-semibold">
                          <CurrencyDisplay
                            amount={remDue}
                            hideSymbol
                            className={remDue > 0 ? "text-danger-600" : "text-foreground-400"}
                          />
                        </td>
                        <td className="py-2 px-3 text-center">
                          {isCovered ? (
                            <Badge color="success">Covered</Badge>
                          ) : isPartial ? (
                            <Badge color="warning">Partial</Badge>
                          ) : (
                            <Badge color="neutral">Needs Cash</Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
